// watch/ClipDialog.tsx — Clip (1.27.60, concept action row "Clip": "select a short section of
// the timeline to share"). Pick where it starts and how long it runs (5–60 s), preview it on a
// loop, add a note, share. Nothing is uploaded: the clip post points at this video (owner
// ruling "a reference into the source video").
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Dialog, MetaRow, useToast } from '@/ui';
import { usePosts } from '@/hooks';
import type { Post } from '@/core/models/post';
import { formatClock } from '../../player/chapters';

const CLIP_MIN_SEC = 5;
const CLIP_MAX_SEC = 60;

const Body = styled.div`
  display: grid;
  gap: 12px;
  width: 100%;
  min-width: 0;
  video {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 10px;
    background: #000;
    object-fit: contain;
  }
  label {
    display: grid;
    gap: 4px;
    font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textPrimary};
    font-variant-numeric: tabular-nums;
  }
  input[type='range'] {
    width: 100%;
    accent-color: ${({ theme }) => theme.color.accentCyan};
  }
  textarea {
    width: 100%;
    min-height: 70px;
    padding: 8px 10px;
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
    font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    resize: vertical;
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  span {
    font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export function ClipDialog({
  post,
  title,
  fromSec,
  onClose,
}: {
  post: Post;
  title: string;
  /** Where the watch page's player was — the clip starts there. */
  fromSec: number;
  onClose: () => void;
}) {
  const video = post.video!;
  const duration = video.durationMs / 1000;
  const lastStart = Math.max(0, Math.floor(duration - CLIP_MIN_SEC));
  const [start, setStart] = useState(() => clamp(Math.floor(fromSec), 0, lastStart));
  const maxLength = Math.floor(Math.min(CLIP_MAX_SEC, duration - start));
  const [wanted, setWanted] = useState(15);
  const length = clamp(wanted, CLIP_MIN_SEC, Math.max(CLIP_MIN_SEC, maxLength));
  const end = start + length;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const previewRef = useRef<HTMLVideoElement>(null);
  const { createPost } = usePosts();
  const toast = useToast();
  const navigate = useNavigate();

  // The preview jumps to the section whenever it moves.
  useEffect(() => {
    const el = previewRef.current;
    if (el && Math.abs(el.currentTime - start) > 0.25) el.currentTime = start;
  }, [start]);

  function togglePreview() {
    const el = previewRef.current;
    if (!el) return;
    if (el.paused) {
      if (el.currentTime < start || el.currentTime >= end) el.currentTime = start;
      void el.play().catch(() => {});
    } else el.pause();
  }

  async function share() {
    setBusy(true);
    try {
      const created = await createPost({
        body: note.trim(),
        clipOf: post.shareCode ?? post.id,
        clipStartMs: Math.round(start * 1000),
        clipEndMs: Math.round(end * 1000),
      });
      toast('Clip shared', { label: 'View', onClick: () => navigate(`/videos/${created.id}`) });
      onClose();
    } catch {
      toast('The clip couldn’t be shared');
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title="Clip">
      <Body>
        <video
          ref={previewRef}
          src={video.lightUrl}
          poster={video.posterUrl ?? undefined}
          playsInline
          preload="metadata"
          aria-label={`Preview of the clip from ${title}`}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => {
            const el = e.currentTarget;
            if (el.currentTime >= end) el.currentTime = start; // loop the section
          }}
        />
        <Row>
          <Button variant="secondary" size="sm" onClick={togglePreview}>
            {playing ? 'Pause preview' : 'Play preview'}
          </Button>
          <MetaRow>
            <span>
              {formatClock(start)} – {formatClock(end)}
            </span>
            <span>{length} seconds</span>
          </MetaRow>
        </Row>
        <label>
          Starts at {formatClock(start)}
          <input
            type="range"
            min={0}
            max={lastStart}
            step={1}
            value={start}
            aria-label="Clip starts at"
            aria-valuetext={formatClock(start)}
            onChange={(e) => setStart(Number(e.target.value))}
          />
        </label>
        <label>
          Length: {length} seconds
          <input
            type="range"
            min={CLIP_MIN_SEC}
            max={Math.max(CLIP_MIN_SEC, maxLength)}
            step={1}
            value={length}
            aria-label="Clip length in seconds"
            onChange={(e) => setWanted(Number(e.target.value))}
          />
        </label>
        <textarea
          aria-label="Say something about this clip (optional)"
          placeholder="Say something about this clip (optional)"
          maxLength={5000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Row>
          <span>Shared as a post that plays this part of {title}.</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => void share()} disabled={busy}>
              Share clip
            </Button>
          </div>
        </Row>
      </Body>
    </Dialog>
  );
}
